import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../App";
import { ArrowLeft, Clock, BookOpen, Gamepad2, Brain, Zap, ChevronDown, CheckCircle, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  genesis: "border-[#F7931A] bg-[#F7931A]/10",
  milestone: "border-purple-500 bg-purple-500/10",
  price: "border-green-500 bg-green-500/10",
  halving: "border-orange-500 bg-orange-500/10",
  regulation: "border-red-500 bg-red-500/10",
  security: "border-yellow-500 bg-yellow-500/10",
  technology: "border-[#00E5FF] bg-[#00E5FF]/10",
  macro: "border-blue-500 bg-blue-500/10",
  institutional: "border-emerald-500 bg-emerald-500/10",
  personal: "border-[#F7931A] bg-[#F7931A]/20"
};

const CATEGORY_DOT_COLORS = {
  genesis: "bg-[#F7931A]",
  milestone: "bg-purple-500",
  price: "bg-green-500",
  halving: "bg-orange-500",
  regulation: "bg-red-500",
  security: "bg-yellow-500",
  technology: "bg-[#00E5FF]",
  macro: "bg-blue-500",
  institutional: "bg-emerald-500",
  personal: "bg-[#F7931A]"
};

export default function Timeline() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("timeline"); // "timeline" or "quiz"
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [answerState, setAnswerState] = useState("idle"); // idle, evaluating, revealed

  useEffect(() => {
    api.get("/timeline/events").then(res => {
      setEvents(res.data.events);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const startQuiz = async () => {
    try {
      const res = await api.get("/timeline/quiz");
      setQuizQuestions(res.data.questions);
      setCurrentQ(0);
      setSelectedAnswer(null);
      setQuizScore(0);
      setQuizDone(false);
      setAnswerState("idle");
      setMode("quiz");
    } catch {
      toast.error("Failed to load quiz");
    }
  };

  const handleAnswer = (answer) => {
    if (answerState !== "idle") return;
    
    setSelectedAnswer(answer);
    setAnswerState("evaluating");
    
    // 500ms evaluation pause
    setTimeout(() => {
      setAnswerState("revealed");
      const isCorrect = answer === quizQuestions[currentQ].correct;
      if (isCorrect) setQuizScore(s => s + 1);
      
      // Transition to next question
      setTimeout(() => {
        if (currentQ < quizQuestions.length - 1) {
          setCurrentQ(c => c + 1);
          setSelectedAnswer(null);
          setAnswerState("idle");
        } else {
          setQuizDone(true);
        }
      }, 1200);
    }, 500);
  };

  const categories = ["all", ...new Set(events.map(e => e.category))];
  const filtered = filter === "all" ? events : events.filter(e => e.category === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="timeline-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => mode === "quiz" ? setMode("timeline") : navigate("/dashboard")} className="text-zinc-400 hover:text-white" data-testid="timeline-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">BITCOIN <span className="text-[#F7931A]">TIMELINE</span></h1>
          <Clock className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {mode === "timeline" && (
          <>
            {/* Mode toggle and actions */}
            <div className="flex gap-2 mb-4">
              <Button onClick={startQuiz} className="bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/30 hover:bg-[#F7931A]/30" data-testid="timeline-quiz-btn">
                <Brain size={16} className="mr-2" /> Timeline Quiz
              </Button>
              <Button onClick={() => navigate("/games/simulator")} className="bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30" data-testid="timeline-simulator-btn">
                <Gamepad2 size={16} className="mr-2" /> Simulator
              </Button>
            </div>

            {/* Category filter */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    filter === cat ? "bg-[#F7931A] text-black" : "bg-[#161616] text-zinc-400 border border-white/10"
                  }`}
                  data-testid={`timeline-filter-${cat}`}
                >
                  {cat === "all" ? "All" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>

            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#F7931A] via-[#F7931A]/50 to-transparent" />
              <div className="space-y-4">
                {filtered.map((event, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }} className="relative pl-14">
                    <div className={`absolute left-4 top-4 w-4 h-4 rounded-full border-2 border-[#050505] ${CATEGORY_DOT_COLORS[event.category] || "bg-[#F7931A]"}`} />
                    <div
                      className={`card-dark p-4 cursor-pointer border ${expanded === i ? CATEGORY_COLORS[event.category] || "" : ""} hover:border-[#F7931A]/30 transition-all`}
                      onClick={() => setExpanded(expanded === i ? null : i)}
                      data-testid={`timeline-event-${i}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-[#F7931A] font-mono text-xs">{event.date}</p>
                          <h3 className="text-white font-heading font-bold text-sm mt-1">{event.title}</h3>
                        </div>
                        <span className="text-zinc-600 text-xs capitalize bg-zinc-800 px-2 py-0.5 rounded">{event.category}</span>
                      </div>
                      {expanded === i && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3">
                          <p className="text-zinc-300 text-sm leading-relaxed">{event.description}</p>
                          {event.course_sections?.length > 0 && (
                            <div className="flex gap-1 mt-3 items-center">
                              <BookOpen className="text-zinc-500" size={14} />
                              {event.course_sections.map(s => (
                                <button key={s} onClick={(e) => { e.stopPropagation(); navigate(`/course/section/${s}`); }}
                                  className="text-[#F7931A] text-xs hover:underline">
                                  Section {s}
                                </button>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Quiz Mode */}
        {mode === "quiz" && !quizDone && quizQuestions.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto space-y-6">
            <div className="flex justify-between items-center">
              <span className="text-zinc-500 text-sm">Question {currentQ + 1} of {quizQuestions.length}</span>
              <span className="text-[#F7931A] font-mono text-sm">{quizScore}/{currentQ + (selectedAnswer ? 1 : 0)}</span>
            </div>

            <div className="card-dark p-6 border border-[#F7931A]/20">
              <h3 className="text-white font-medium text-lg mb-4" data-testid="quiz-question">{quizQuestions[currentQ].question}</h3>
              <div className="space-y-2">
                {quizQuestions[currentQ].options.map((opt, i) => {
                  const isRevealed = answerState === "revealed";
                  const isCorrectOpt = isRevealed && opt === quizQuestions[currentQ].correct;
                  const isSelected = selectedAnswer === opt;
                  const isWrongOpt = isRevealed && isSelected && opt !== quizQuestions[currentQ].correct;
                  const isEval = answerState === "evaluating" && isSelected;
                  
                  return (
                    <button key={i} onClick={() => handleAnswer(opt)}
                      disabled={answerState !== "idle"}
                      className={`w-full text-left p-3 rounded-lg border transition-all duration-500 flex items-center gap-3 ${
                        isCorrectOpt ? "bg-green-500/10 border-green-500/50" :
                        isWrongOpt ? "bg-red-500/10 border-red-500/50" :
                        isEval ? "border-[#F7931A] bg-[#F7931A]/10" :
                        "border-white/10 hover:border-[#F7931A]/30"
                      }`}
                      data-testid={`quiz-option-${i}`}>
                      {isCorrectOpt && <CheckCircle className="text-green-400 flex-shrink-0" size={16} />}
                      {isWrongOpt && <XCircle className="text-red-400 flex-shrink-0" size={16} />}
                      <span className={`text-sm ${isCorrectOpt ? "text-green-400" : isWrongOpt ? "text-red-400" : "text-zinc-300"}`}>{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-[#161616] rounded-full overflow-hidden">
              <div className="h-full bg-[#F7931A] transition-all" style={{ width: `${((currentQ + 1) / quizQuestions.length) * 100}%` }} />
            </div>
          </motion.div>
        )}

        {/* Quiz Results */}
        {mode === "quiz" && quizDone && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg mx-auto text-center space-y-6 py-12">
            <div className="w-20 h-20 rounded-2xl bg-[#F7931A]/20 flex items-center justify-center mx-auto">
              <Brain className="text-[#F7931A]" size={36} />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white">Timeline Quiz Complete!</h2>
            <p className="text-[#F7931A] font-mono text-4xl font-bold">{quizScore}/{quizQuestions.length}</p>
            <p className="text-zinc-400">
              {quizScore === quizQuestions.length ? "Perfect score! You know Bitcoin history inside and out." :
               quizScore >= quizQuestions.length * 0.6 ? "Great work! You know your Bitcoin history." :
               "Keep learning! Explore the timeline to discover more."}
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={startQuiz} className="bg-[#F7931A] text-black hover:bg-[#F7931A]/90" data-testid="quiz-retry-btn">
                Try Again
              </Button>
              <Button onClick={() => setMode("timeline")} variant="outline" className="border-white/20 text-zinc-400 hover:text-white" data-testid="quiz-back-btn">
                Back to Timeline
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
